import { MainTemplate } from "@/components/templates";
import { Global, css } from "@emotion/react";
import { color } from "@/styles/theme";
import { HorizontalRule } from "@/components/atoms/terms/HorizontalRule";
import { RevisionHistory } from "@/components/atoms/terms/RevisionHistory";
import config from "@/libs/config";
import { Font } from "@moneyforward/mfx_biz_react_components";
import { ReactNode } from "react";

export const TermsLayout = ({
  title,
  renderIntroduction,
  renderArticles,
}: {
  title: string;
  renderIntroduction: () => ReactNode;
  renderArticles: () => ((index: number) => ReactNode)[];
}) => {
  return (
    <>
      <Global styles={resetStyle} />
      <MainTemplate term={true} title={title}>
        <div css={termsLayoutStyle}>
          <div css={termsLayoutInnerStyle}>
            <header>
              <Font component="h1" variant="lb">
                {title}
              </Font>
            </header>
            <div css={contentsStyle}>
              <div css={contentsInnerStyle}>
                <section>{renderIntroduction()}</section>
                <section>
                  <ul css={articlesStyle}>
                    {renderArticles().map((renderArticle, i) => (
                      <li key={i}>{renderArticle(i + 1)}</li>
                    ))}
                  </ul>
                </section>
              </div>
              <div css={dividerStyle}>
                <HorizontalRule />
              </div>
              <footer css={contentsFooterStyle}>
                <RevisionHistory
                  regulationDateString={config.REGULATION_DATE}
                  revisionDateString={config.REVISION_DATE}
                />
              </footer>
            </div>
          </div>
        </div>
      </MainTemplate>
    </>
  );
};

/**
 * Style definition for resetting the base style. Ideally, it should not be defined limited to specific components,
 * but due to the extensive impact when applied globally, it is defined here as a temporary measure.
 */
const resetStyle = css`
  ul {
    margin: 0;
    padding: 0;
  }
  ol {
    margin: 0;
    padding: 0;
    /* 
     * When the number of list items exceeds 10, the index indent extends one character to the left. 
     * Therefore, in such cases, consider an alternative approach.
     */
    padding-left: 1.6rem;
  }
`;

const termsLayoutStyle = css`
  display: flex;
  justify-content: space-evenly;
`;

const termsLayoutInnerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  max-width: 70.4rem;

  /* The padding value of the encompassing container is larger than the design, so it needs to be offset. */
  margin-top: -1rem;
`;

const contentsStyle = css`
  padding: 3.2rem 2.4rem;
  border-radius: 0.2rem;
  border: 1px solid ${color.gray300};
  background: ${color.white};
`;

const contentsInnerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
`;

const articlesStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
`;

const dividerStyle = css`
  margin-top: 5.6rem;
`;

const contentsFooterStyle = css`
  margin-top: 1.9rem;
`;
